import { useTranslation } from 'react-i18next'
import { Icon } from '@/components/ui/icon'

const EVIDENCE_KEYS = ['reporting', 'sidekick', 'plan'] as const

/**
 * The free floor, as a dated ledger.
 *
 * The hero opens with a claim that costs the reader money if it is wrong, so
 * this section has to pay for it. It is the one place on the page where the
 * product's differentiating behaviour is literally visible: every row carries
 * the date it was observed and a link to the platform's own words.
 *
 * That is why the date is set as the largest element in the row rather than as
 * grey caption text. The dates are the evidence. Treating them as metadata
 * would hide the only thing here a reader can check.
 */
export function Evidence() {
  const { t } = useTranslation()

  return (
    <section
      id="evidence"
      aria-labelledby="evidence-title"
      className="scroll-mt-24 border-t py-20 sm:py-28"
      style={{ borderColor: 'var(--lp-rule)' }}
    >
      <div className="mx-auto w-full max-w-6xl px-6 lg:px-10">
        <h2
          id="evidence-title"
          className="max-w-[44rem] text-balance text-3xl font-semibold tracking-[-0.025em] sm:text-4xl"
          style={{ color: 'var(--lp-ink)' }}
        >
          {t('landing:evidence.title')}
        </h2>
        <p
          className="mt-4 max-w-[36rem] text-pretty text-lg leading-relaxed"
          style={{ color: 'var(--lp-body)' }}
        >
          {t('landing:evidence.description')}
        </p>

        <ol
          className="mt-12 border-t sm:mt-14"
          style={{ borderColor: 'var(--lp-rule-strong)' }}
        >
          {EVIDENCE_KEYS.map((key) => {
            const dateLabelKey = t(`landing:evidence.items.${key}.date_label`)
            return (
              <li
                key={key}
                className="grid gap-x-10 gap-y-4 border-b py-8 md:grid-cols-[9rem_minmax(0,1fr)] lg:grid-cols-[11rem_minmax(0,1fr)_max-content] md:py-10"
                style={{ borderColor: 'var(--lp-rule)' }}
              >
                <div className="flex items-baseline gap-3 md:block">
                  <time
                    dateTime={t(`landing:evidence.items.${key}.date`)}
                    className="lp-figure block text-xl font-medium sm:text-[1.375rem]"
                    style={{ color: 'var(--lp-ink)' }}
                  >
                    {t(`landing:evidence.items.${key}.date`)}
                  </time>
                  <span
                    className="block text-sm md:mt-1.5"
                    style={{ color: 'var(--lp-meta)' }}
                  >
                    {t(`landing:evidence.date_labels.${dateLabelKey}`)}
                  </span>
                </div>

                <p
                  className="text-pretty text-lg leading-[1.6]"
                  style={{ color: 'var(--lp-ink)' }}
                >
                  {t(`landing:evidence.items.${key}.claim`)}
                </p>

                {/*
                 * The source is the third field of the record, not a footnote
                 * to the claim. Sitting at the end of its own column keeps the
                 * three links aligned down the page, so a reader can check all
                 * of them without hunting for where each one starts.
                 */}
                <a
                  href={t(`landing:evidence.items.${key}.href`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lp-link-free inline-flex items-center gap-1.5 self-start justify-self-start text-sm font-semibold md:col-start-2 lg:col-start-3 lg:justify-self-end"
                  style={{ color: 'var(--lp-free)' }}
                >
                  {t(`landing:evidence.items.${key}.source_label`)}
                  <Icon name="actions.externalLink" size="sm" aria-hidden />
                </a>
              </li>
            )
          })}
        </ol>

        <p
          className="mt-6 max-w-[38rem] text-sm md:ml-[calc(9rem+2.5rem)] lg:ml-[calc(11rem+2.5rem)]"
          style={{ color: 'var(--lp-meta)' }}
        >
          {t('landing:evidence.footnote')}
        </p>
      </div>
    </section>
  )
}
